// Eager slim slice of the canvas function barrel (#15). See
// components/eagerSlice.js for the full rationale — same carve: the shell
// imports this instead of `../canvas/functions/index.js` so the heavy
// canvas-editor function graph stays out of the eager chunk.
//
// Each source file below is light (no CanvasEditor/Scene/Monaco imports) and is
// `export *`-ed from source, never through `./index.js` (which fans out to the
// whole editor). Because canvas has no `sideEffects: false`, referencing any
// export of a source file bundles that whole (small) module regardless, so
// `export *` costs no more than a named re-export while re-exposing a strict
// superset of the keys the eager routes need — minimising the "missing key
// silently breaks a bare-key extends/child" risk.
//
// Required keys and their source files:
//   toggleSidebars, getSelectionAiContext                          → projectCanvasSidebar.js
//   renderAnswerUi                                                 → answerUi.js
//   injectPageInApp                                                → injectPageInApp.js
//   parseFreestyleSource                                           → freestyle.js
//   initPanzoomBoard                                               → initPanzoomBoard.js
//   registerCanvasPanzoom, unregisterCanvasPanzoom                 → registerCanvasPanzoom.js
//   bboxOfNodeIds, bezierBetweenNodes                              → sharedGeom.js
//   tagNodeContextmenu, tagNodeContextItems, tagNodeContextAction  → tagContextMenu.js
//   isCanvasV2                                                     → canvas/state.js
//   confirmTeamLeave, runTeamLeave                                 → teamLeave.js
//   confirmAgentUnassign                                           → agentUnassign.js
//
// tagNodeContextItems + tagNodeContextAction are string-dispatched through
// `root.contextDropdown` in tagContextMenu.js (invisible to static analysis but
// required by the /team-and-agents + /agents node context menu).
export * from './projectCanvasSidebar.js'
export * from './answerUi.js'
export * from './injectPageInApp.js'
export * from './freestyle.js'
export * from './initPanzoomBoard.js'
export * from './registerCanvasPanzoom.js'
export * from './sharedGeom.js'
export * from './tagContextMenu.js'
export * from './canvas/state.js'
// Entity (data-item) nodes — spec §3. `focusCanvasEntity` MUST be eager: the
// assistant's source chips re-focus a canvas node from the shell drawer, which
// lives on routes that never fold the canvas manifest, so a lazy registration
// would resolve to `undefined` there.
//
// The ref parser/formatter are NOT listed here and must not be: they are the
// shared contract (`@symbo-ls/workspace-shared/functions/entityRef.js`), which
// the shell registers globally, so the drawer already resolves a ref before it
// knows whether a canvas is open. Re-exporting them from this package would
// shadow the canonical names in the merged context (Rule 63).
//
// Both modules below import NOTHING (they reach the SDK through the element's
// own `el.sdk` and their siblings through `el.call`), so the eager chunk grows
// by two small self-contained files.
export * from './canvas/entityLayout.js'
export * from './canvas/entity.js'
// parseVoiceCommand + voiceProjectCommand — the voice command layer. It must
// be EAGER because talk mode (the only conversational-engine-eligible mode)
// runs from the assistant chat composer, which lives on shell routes that
// never fold the canvas manifest. Without this the ElevenLabs client tool
// `runCanvasCommand` answered "There is no canvas on this screen." every
// single time, so "a conversation that also acts" could never act, and Web
// Speech talk silently skipped its grammar check too. Costs nothing: the
// module has ZERO imports (233 lines of pure grammar + guarded el.call
// dispatch) and its own header already anticipates off-canvas shell routes —
// each target is guarded so unmatched intents fall through gracefully.
export * from './voiceCommands.js'
// The `canvas-projects` and `project-status` AI-answer views' provider, reads
// and cards: the shell reads the provider on ANY route (workspace functions/
// assistantAppViews.js) and the views mount without a fold. Zero imports.
export * from './canvasAnswer.js'
export * from './canvasStatus.js'
// studioOpenProject + isStudioAiBuiltApp — one way to open a project: an
// AI-built app in /add-app, every other project on its route. EAGER because
// the AI answer card above (components/CanvasProjectsView.js, in the eager
// component slice) opens a project through it on any route. Zero imports.
export * from './studioOpenProject.js'
// confirmTeamLeave + runTeamLeave — a drop outside every team frame on
// /team-and-agents asks before it removes the member. EAGER because that page
// is in the eager component slice (components/eagerSlice.js) and calls them by
// name. Zero imports.
export * from './teamLeave.js'
// confirmAgentUnassign — the same page's agent → person edge and the agent
// editor's Remove ask before they unassign. Zero imports.
export * from './agentUnassign.js'
// The panes' back/forward history (pure data → data, zero imports). The
// shell's phone Back on a full-page app route steps the SAME history shape
// (workspace functions/phoneNavigation.js phoneNavigationBack, Nika
// 2026-10-31), on routes that never fold the canvas manifest. The same named
// set the full barrel (./index.js) exports, so the fold adds nothing new.
export {
  histInit,
  histCurrent,
  histRecord,
  histCanBack,
  histCanForward,
  histBack,
  histForward
} from './slotHistory.js'
